/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Globe, Users, Share2, FileText, MapPin, Phone, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const SOURCE_ICONS = {
    site_internet: Globe,
    instagram: Share2,
    facebook: Share2,
    bouche_a_oreille: Users,
    recommandation: Users,
    pige: FileText,
    prospection: MapPin,
    panneau: MapPin,
    partenaire: Users,
    autre: Phone,
    non_renseigne: FileText
};

const SOURCE_COLORS = {
    site_internet: 'bg-blue-500',
    instagram: 'bg-pink-500',
    facebook: 'bg-blue-600',
    bouche_a_oreille: 'bg-green-500',
    recommandation: 'bg-emerald-500',
    pige: 'bg-yellow-500',
    prospection: 'bg-orange-500',
    panneau: 'bg-purple-500',
    partenaire: 'bg-cyan-500',
    autre: 'bg-gray-500',
    non_renseigne: 'bg-gray-600'
};

export default function LeadSourcesKPI() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('12months');

    useEffect(() => { load(); }, [period]);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/agency/lead-sources?period=${period}`);
            setData(res.data);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };

    if (loading) return <div className="animate-pulse bg-card rounded-2xl h-48 border border-border" />;
    if (!data) return null;

    const maxCount = Math.max(...data.sources.map(s => s.count), 1);

    return (
        <div className="bg-card rounded-2xl border border-border overflow-hidden" data-testid="lead-sources-kpi">
            <div className="p-5 border-b border-border flex items-center justify-between">
                <div>
                    <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                        <Globe className="w-4 h-4 text-[#FF914D]" />
                        Sources d'acquisition
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">D'où viennent vos mandats ?</p>
                </div>
                <div className="flex gap-1">
                    {[{v: '3months', l: '3M'}, {v: '6months', l: '6M'}, {v: '12months', l: '12M'}].map(p => (
                        <button key={p.v} onClick={() => setPeriod(p.v)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${period === p.v ? 'bg-[#FF914D]/20 text-[#FF914D]' : 'text-muted-foreground hover:bg-white/5'}`}
                        >{p.l}</button>
                    ))}
                </div>
            </div>

            <div className="p-5 space-y-4">
                {/* Global sources bar chart */}
                <div className="space-y-2">
                    {data.sources.map((s) => {
                        const Icon = SOURCE_ICONS[s.source] || FileText;
                        const color = SOURCE_COLORS[s.source] || 'bg-gray-500';
                        return (
                            <div key={s.source} className="flex items-center gap-3">
                                <div className="w-24 text-right flex items-center gap-1.5 justify-end shrink-0">
                                    <Icon className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-[10px] text-muted-foreground truncate">{s.label}</span>
                                </div>
                                <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden relative">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${(s.count / maxCount) * 100}%` }}
                                        transition={{ duration: 0.6, delay: 0.1 }}
                                        className={`h-full ${color} rounded-full`}
                                    />
                                </div>
                                <div className="w-20 flex items-center gap-2 shrink-0">
                                    <span className="text-xs font-bold text-foreground">{s.count}</span>
                                    <span className="text-[10px] text-muted-foreground">({s.percentage}%)</span>
                                </div>
                                <div className="w-14 shrink-0 text-right">
                                    <span className={`text-[10px] font-bold ${s.conversion_rate > 30 ? 'text-green-400' : s.conversion_rate > 0 ? 'text-yellow-400' : 'text-white/20'}`}>
                                        {s.conversion_rate}% conv.
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Best source highlight */}
                {data.best_converting_source && (
                    <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                        <p className="text-xs text-green-400 font-bold">
                            Meilleure source : {data.best_converting_source.label} ({data.best_converting_source.conversion_rate}% de conversion)
                        </p>
                    </div>
                )}

                {/* Per agent breakdown */}
                {data.agents_sources.length > 0 && (
                    <div>
                        <h4 className="text-xs font-bold text-muted-foreground mb-2">Par agent</h4>
                        <div className="space-y-1.5">
                            {data.agents_sources.map((a, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                    <span className="text-foreground font-medium w-28 truncate">{a.name}</span>
                                    <div className="flex-1 flex gap-1 flex-wrap">
                                        {Object.entries(a.sources).sort((a, b) => b[1] - a[1]).map(([src, count]) => (
                                            <span key={src} className={`px-1.5 py-0.5 rounded text-[10px] ${SOURCE_COLORS[src] || 'bg-gray-600'} text-white`}>
                                                {data.source_labels[src] || src}: {count}
                                            </span>
                                        ))}
                                    </div>
                                    <span className="text-muted-foreground text-[10px]">{a.total} total</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
